import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Minus, Plus, Star, ShieldCheck, Truck, FileCheck2 } from "lucide-react";
import api, { formatMoney, originalPrice, DISCOUNT_LABEL, bogoTier } from "../lib/apiClient";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem, bogo, saleOn } = useCart();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [variantIdx, setVariantIdx] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setProduct(r.data); setVariantIdx(0); }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-24 text-center">
        <p className="text-muted-foreground">Product not found.</p>
        <Link to="/shop" className="font-semibold mt-4 inline-block" style={{ color: "var(--gold)" }}>Back to shop</Link>
      </div>
    );
  }
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-24 text-center text-muted-foreground">Loading…</div>;

  const strengths = product.strengths?.length ? product.strengths : [{ label: "Default", price: product.price }];
  const variant = strengths[variantIdx] || strengths[0];
  const unitPrice = variant.price || product.price;
  const showVariants = strengths.length > 1 || (strengths[0] && strengths[0].label !== "Default");
  const displayImage = variant.image_url || product.image_url;

  const coaCerts = product.coa?.certificates || [];
  const vLabel = (variant.label || "").toUpperCase().replace(/\s+/g, "");
  const matchedCoa = coaCerts.filter((c) => (c.strength || "").toUpperCase().replace(/\s+/g, "") === vLabel);
  const tileCoa = matchedCoa[0] || (product.coa?.certificate_url ? { certificate_url: product.coa.certificate_url, purity: product.coa.purity } : null);
  const fallbackCount = product.coa?.certificates?.length || 0;

  const tier = bogo ? bogoTier(qty) : { free: 0, pct: 0 };
  const bogoPaid = Math.round(unitPrice * qty * (1 - tier.pct) * 100) / 100;
  const bogoFullValue = unitPrice * (qty + tier.free);
  const bogoSavePct = bogoFullValue > 0 ? Math.round((1 - bogoPaid / bogoFullValue) * 100) : 0;
  const bogoNudge = !bogo
    ? null
    : qty === 1
    ? "Add 1 more to unlock 2 free + 5% off"
    : qty >= 2 && qty < 10
    ? `Reach 10 to unlock 20% off (you're at ${Math.round(tier.pct * 100)}%)`
    : null;

  const handleAdd = () => {
    addItem(product, qty, variant);
    toast.success(`${product.name}${variant.label && variant.label !== "Default" ? ` (${variant.label})` : ""} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <button onClick={() => navigate(-1)} data-testid="back-button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="rounded-3xl card-soft overflow-hidden bg-[#f6f1e6] animate-fade-up">
          {saleOn && <span className="absolute m-4 pill px-3 py-1 text-[11px] font-display font-bold text-[#1a1815] z-10" style={{ background: "var(--gold)" }}>{DISCOUNT_LABEL}</span>}
          <img src={displayImage} alt={product.name} className="w-full h-full object-cover aspect-square" data-testid="product-detail-image" />
        </div>

        <div className="animate-fade-up" style={{ animationDelay: "80ms" }}>
          <span className="eyebrow">{product.category}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2" data-testid="product-detail-name">{product.name}</h1>
          {product.subtitle && <p className="text-lg text-muted-foreground mt-1">{product.subtitle}</p>}
          <div className="flex items-center gap-2 mt-3 text-sm">
            <span className="flex items-center gap-1" style={{ color: "var(--gold)" }}>
              <Star className="w-4 h-4 fill-current" /> {product.rating}
            </span>
          </div>

          <p className="text-muted-foreground leading-relaxed mt-5">{product.description}</p>

          <div className="mt-6 flex items-end gap-2 flex-wrap">
            {saleOn && <span className="font-display font-extrabold text-2xl text-muted-foreground line-through" data-testid="product-detail-original-price">{formatMoney(originalPrice(unitPrice))}</span>}
            <span className="font-display font-extrabold text-4xl" data-testid="product-detail-price">{formatMoney(unitPrice)}</span>
            {saleOn && <span className="pill px-2.5 py-1 text-[11px] font-display font-bold text-[#1a1815] mb-1.5" style={{ background: "var(--gold)" }} data-testid="product-detail-discount-badge">{DISCOUNT_LABEL}</span>}
            <span className="text-sm text-muted-foreground mb-1.5">per vial</span>
          </div>

          {bogo && (
            <div className="mt-6 rounded-2xl border border-[#e6c665]/60 bg-gradient-to-b from-white to-[#fdf6e3] p-5" data-testid="product-bogo">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="eyebrow text-[#8a681c] tracking-[0.2em]">BOGO Sale — Buy X Get X Free</span>
                {tier.pct > 0 && (
                  <span className="pill px-2.5 py-1 text-[11px] font-display font-bold text-[#1a1815]" style={{ background: "var(--gold)" }} data-testid="product-bogo-save">SAVE {bogoSavePct}%</span>
                )}
              </div>
              {bogoNudge && <p className="text-sm text-[#6b5a2e] mt-1" data-testid="product-bogo-nudge">{bogoNudge}</p>}

              <div className="grid grid-cols-3 gap-2.5 mt-4" data-testid="product-bogo-tiers">
                {[1, 2, 3].map((n) => {
                  const t = bogoTier(n);
                  const active = qty === n;
                  return (
                    <button
                      key={n}
                      onClick={() => setQty(n)}
                      data-testid={`product-bogo-tier-${n}`}
                      className={`relative text-center rounded-2xl border-2 px-2 pt-6 pb-3 transition-all ${active ? "border-[#1a1815] bg-white shadow-lg -translate-y-0.5" : "border-border bg-white/70 hover:border-[#c59b27]/70"}`}
                    >
                      <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[9px] font-display font-bold tracking-wide ${active ? "text-white bg-[#1a1815]" : "text-[#6b5a2e] bg-[#f4e7c4]"}`}>BUY {n} GET {n} FREE</span>
                      <div className="flex items-center justify-center gap-0.5 flex-wrap min-h-[1.75rem]">
                        {Array.from({ length: n }).map((_, i) => <img key={i} src={displayImage} alt="" className="w-6 h-6 object-contain" />)}
                      </div>
                      <p className="text-xs font-display font-bold mt-1">BUY {n}</p>
                      <div className="h-px bg-border my-2" />
                      <div className="flex items-center justify-center gap-0.5 flex-wrap min-h-[1.75rem]">
                        {Array.from({ length: t.free }).map((_, i) => <img key={i} src={displayImage} alt="" className="w-6 h-6 object-contain" />)}
                      </div>
                      <p className="text-xs font-display font-bold text-[#2f7a3f] mt-1">GET {t.free} FREE</p>
                      {t.pct > 0 && <p className="text-[11px] font-display font-bold text-[#8a681c] mt-0.5">+ {Math.round(t.pct * 100)}% OFF</p>}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setQty(10)}
                data-testid="product-bogo-tier-10"
                className={`relative w-full text-center rounded-2xl border-2 px-3 pt-6 pb-4 mt-4 transition-all ${qty >= 10 ? "border-[#1a1815] bg-white shadow-lg" : "border-border bg-white/70 hover:border-[#c59b27]/70"}`}
              >
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-0.5 text-[10px] font-display font-bold text-[#1a1815]" style={{ background: "var(--gold)" }}>10 FREE VIALS + 20% OFF</span>
                <div className="flex items-center justify-center gap-0.5 flex-wrap">
                  {Array.from({ length: 10 }).map((_, i) => <img key={i} src={displayImage} alt="" className="w-6 h-6 object-contain" />)}
                </div>
                <p className="text-base font-display font-extrabold mt-1">BUY 10</p>
                <div className="h-px bg-border my-2" />
                <div className="flex items-center justify-center gap-0.5 flex-wrap">
                  {Array.from({ length: 10 }).map((_, i) => <img key={i} src={displayImage} alt="" className="w-6 h-6 object-contain" />)}
                </div>
                <p className="text-sm font-display font-bold text-[#2f7a3f] mt-1">+ GET 10 FREE · 20% OFF</p>
              </button>

              <p className="text-sm mt-4 text-center text-[#3a2f12]" data-testid="product-bogo-summary">
                You picked <b>{qty}</b> — get <b className="text-[#2f7a3f]">{tier.free} free</b>{tier.pct > 0 ? ` + ${Math.round(tier.pct * 100)}% off` : ""} → <b>{qty + tier.free} vials</b> for {formatMoney(bogoPaid)}.
              </p>
            </div>
          )}

          {showVariants && (
            <div className="mt-5">
              <p className="eyebrow mb-2">Size</p>
              <div className="flex flex-wrap gap-2">
                {strengths.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setVariantIdx(i)}
                    data-testid={`variant-${i}`}
                    className={`pill px-4 py-2 text-sm font-semibold border transition-colors ${i === variantIdx ? "btn-ink border-transparent" : "bg-white border-border hover:border-foreground/40"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-4 mt-7">
            <div className="flex items-center gap-3 bg-white border border-border rounded-xl px-3 py-2">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="qty-decrease" className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/60">
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} data-testid="qty-increase" className="w-8 h-8 rounded-lg bg-secondary flex items-center justify-center hover:bg-secondary/60">
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button onClick={handleAdd} data-testid={`add-to-cart-button-${product.id}`} className="flex-1 h-12 rounded-xl btn-ink font-display font-bold">
              Add to Cart — {formatMoney(bogo ? bogoPaid : unitPrice * qty)}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8">
            {tileCoa ? (
              <a href={`${process.env.REACT_APP_BACKEND_URL}${tileCoa.certificate_url}`} target="_blank" rel="noopener noreferrer" data-testid="product-coa-link" className="card-soft rounded-xl p-4 text-center border border-[#cfe6cf] bg-[#f3faf3] hover:bg-[#e8f5e8] transition-colors">
                <FileCheck2 className="w-5 h-5 mx-auto text-[#2f7a3f]" />
                <p className="text-xs font-semibold mt-2 text-[#276434]" data-testid="product-coa-purity">{tileCoa.purity ? `${tileCoa.purity} verified` : "Lab verified"}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="product-coa-count">
                  {matchedCoa.length
                    ? (matchedCoa.length > 1 ? `View ${matchedCoa.length} COAs — ${variant.label}` : `View COA — ${variant.label}`)
                    : (fallbackCount > 1 ? `View ${fallbackCount} COAs` : "View COA")}
                </p>
              </a>
            ) : (
              <div className="card-soft rounded-xl p-4 text-center" data-testid="product-coa-pending">
                <FileCheck2 className="w-5 h-5 mx-auto" style={{ color: "var(--gold)" }} />
                <p className="text-xs font-semibold mt-2">COA coming soon</p>
              </div>
            )}
            {[
              { icon: Truck, t: "Free over $200" },
              { icon: ShieldCheck, t: "Secure checkout" },
            ].map((b, i) => (
              <div key={i} className="card-soft rounded-xl p-4 text-center">
                <b.icon className="w-5 h-5 mx-auto" style={{ color: "var(--gold)" }} />
                <p className="text-xs font-semibold mt-2">{b.t}</p>
              </div>
            ))}
          </div>

          <p className="text-xs text-muted-foreground mt-6 bg-secondary/50 rounded-lg p-3">
            For in-vitro laboratory and research use only. Not for human or veterinary use. Not FDA-evaluated.
          </p>
        </div>
      </div>
    </div>
  );
}
